// src/lib/ai-client.js — appels Claude (Anthropic) via le PROXY serveur (Lot B).
//
// Lot B : le front n'appelle PLUS api.anthropic.com directement avec une clé en
// clair. Il passe par api/ai-proxy (requireAdmin) qui détient CLAUDE_API_KEY en
// ENV (lib/ai.js). La clé ne quitte jamais le serveur. Les fonctions de haut
// niveau (analyse/pitch/email) construisent les prompts ici, le proxy relaie.

import { supabase } from "../supabase"
import { resoudreCommission } from "./commission-taux-date"
import { calc } from "./calc"

// Wrapper bas niveau : relaie (system, user, maxTokens) au proxy serveur → texte.
export const callClaude = async (systemPrompt, userPrompt, maxTokens = 1500) => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const res = await fetch("/api/ai-proxy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ system: systemPrompt, user: userPrompt, maxTokens }),
  })
  if (!res.ok) {
    const j = await res.json().catch(() => ({}))
    throw new Error(j.error || `Erreur IA ${res.status}`)
  }
  const data = await res.json()
  return data.text
}

// Filet de sécurité — supprime toute ligne contenant des chiffres financiers
// suspects (commissions, taux, montants) qui ne devraient JAMAIS apparaître
// dans un email de prospection. Garde les n° de téléphone (07/06/+33).
export const sanitizeProspectEmail = (text) => {
  return text
    .split('\n')
    .filter(line => {
      const lower = line.toLowerCase()
      const isTel = /(07|06|\+33)\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}/.test(line)
      if (isTel) return true
      const hasSuspect = (
        /\d+\s*[€%]/.test(line) ||
        /\d{3,}/.test(line) ||
        /(taux|occupation|commission|rendement|formule|confort|smart|zen\+)\s*[:·]?\s*\d/.test(lower) ||
        /\d.*(taux|occupation|commission|rendement)/.test(lower) ||
        /(\+|-)\s*\d+\s*%/.test(line)
      )
      return !hasSuspect
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// ── Analyse business mensuelle (vue compta admin) ──
//
// LOT A4 (30/10) — la commission vient de calc(), donc de la même fonction que
// la facture (src/lib/commission-lignes.js : arrondi par réservation, barème
// daté, dégressif par palier). Avant, elle était recalculée ici : base = CA −
// ménage − TAXE, dégressif ignoré, arrondi à l'euro. Témoin La Douce Escapade
// (l7), septembre 2026 : 493 € annoncés à l'IA contre 438,76 € facturés
// (P-2026-0056). Taux inconnu → « — », et le total n'additionne pas l'inconnu.
const fmtComm = (n) => (n == null ? "—" : `${n.toFixed(2).replace(".", ",")}€`)
const commCalc = (cfg, data, lid, mk) => {
  const log = cfg.props.flatMap((p) => p.logs || []).find((l) => l.id === lid)
  const c = calc(cfg, data, lid, log?.taux, mk)
  return Number.isFinite(c.comm) ? Math.round(c.comm * 100) / 100 : null
}

// Données et texte envoyés à l'IA — fonction pure (testée sans appel réseau).
export const donneesAnalyseBusiness = (cfg, data, month, y, m, incidents) => {
  const propClients = cfg.props.filter(p => p.id !== "p6")
  const monthLabel = `${["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"][m - 1]} ${y}`
  const prevDate = new Date(y, m - 2, 1)
  const prevMk = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`
  const logsData = propClients.flatMap(p => p.logs.map(l => {
    const d = data?.[month]?.[l.id] || {}
    const resas = d.reservations || []
    // Taux affiché : celui de l'époque (lot taux-date) ; « — » s'il manque.
    const tauxMois = resoudreCommission(l, month).taux
    const comm = commCalc(cfg, data, l.id, month)
    const prevComm = commCalc(cfg, data, l.id, prevMk)
    const nuits = resas.reduce((s, r) => s + (r.nuits || 0), 0)
    const occ = Math.round(nuits / new Date(y, m, 0).getDate() * 100)
    const delta = comm == null || prevComm == null ? null : Math.round((comm - prevComm) * 100) / 100
    return { nom: l.nom.split(" ").slice(0, 2).join(" "), proprio: p.nom.split(" ").slice(-1)[0], taux: tauxMois, comm, nuits, occ, delta, incidents: (incidents || []).filter(i => i.logId === l.id).length }
  }))
  const nextDate = new Date(y, m, 1)
  const nextMk = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`
  const nextNuits = propClients.flatMap(p => p.logs).reduce((s, l) => {
    return s + (data?.[nextMk]?.[l.id]?.reservations || []).reduce((ss, r) => ss + (r.nuits || 0), 0)
  }, 0)
  // Un montant inconnu ne s'additionne pas : un seul « — » rend le total inconnu.
  const somme = (champ) => logsData.some(l => l[champ] == null) ? null : Math.round(logsData.reduce((s, l) => s + l[champ], 0) * 100) / 100
  const commTotale = somme("comm")
  const deltaTotal = somme("delta")
  const signe = (n) => (n != null && n > 0 ? "+" : "")
  const user = `Données ${monthLabel} :
Commission totale : ${fmtComm(commTotale)} (${signe(deltaTotal)}${fmtComm(deltaTotal)} vs M-1)
Nuits réservées mois prochain : ${nextNuits}

Par logement :
${logsData.map(l => `- ${l.nom} (${l.taux == null ? "—" : l.taux}%) : ${fmtComm(l.comm)} · ${l.nuits}n · ${l.occ}% occ · ${signe(l.delta)}${fmtComm(l.delta)} · ${l.incidents} incident(s)`).join("\n")}

Donne : 1) Résumé 2 phrases 2) Top performance 3) Point d'attention 4) Action prioritaire (1 seule, concrète) 5) Alerte risque si pertinent. Max 200 mots total.`
  return { logsData, commTotale, user }
}

export const analyseBusinessClaude = async (cfg, data, month, y, m, incidents) => {
  const system = `Tu es un analyste business expert en location courte durée et conciergerie Airbnb. Tu analyses FULLBNBZEN à Toulouse. Tes analyses sont concises et actionnables. Tu parles en "tu" à Néïsia.`
  const { user } = donneesAnalyseBusiness(cfg, data, month, y, m, incidents)
  return await callClaude(system, user, 800)
}

// ── Pitch prospect (cold email) ──
// Agence (template fixe) ou propriétaire (Claude adapte type de bien + quartier).
// Filet de sécurité sanitizeProspectEmail post-Claude pour bloquer les chiffres.
export const genPitchProspectClaude = async (prospect, cfg, data) => {
  const isAgence = prospect.type === "agence" || prospect.source === "Google Maps" || prospect.source === "LinkedIn"
  const typeBien = prospect.typeLogement || "bien"
  const quartier = prospect.adresse ? prospect.adresse.split(" ").slice(-2).join(" ") : "Toulouse"
  const nomAgence = prospect.nom && !["Proprietaire PAP", "Proprietaire LeBonCoin", "Proprietaire SeLoger", "Hote Airbnb"].some(x => prospect.nom.startsWith(x)) ? prospect.nom : ""

  // ── Agence : template fixe, pas de Claude ──
  if (isAgence) {
    const intro = nomAgence ? "Bonjour " + nomAgence + "," : "Bonjour,"
    return intro + `

Je me permets de vous contacter car nos activités pourraient se compléter naturellement.

Nous sommes Dimitri et Néïsia, fondateurs de FULLBNBZEN, une conciergerie Airbnb à Toulouse. Notre activité est totalement différente de la vôtre — nous ne faisons pas de vente ni de location longue durée.

Certains de vos clients ont parfois un bien inoccupé ou souhaitent se lancer dans la location saisonnière sans en gérer l'opérationnel — c'est là que nous pouvons vous être utiles. Nous proposons une collaboration gagnante pour nous deux, avec un partenariat apporteur d'affaires pour les agences qui travaillent avec nous.

Vous pouvez découvrir notre fonctionnement sur fullbnbzen.com.

Seriez-vous disponible pour un café ou un appel afin d'en discuter ?

Bien cordialement,

Dimitri & Néïsia
FULLBNBZEN`
  }

  // ── Propriétaire : Claude adapte la phrase du bien ──
  const systemReflexion = `Tu es un expert en communication commerciale pour FULLBNBZEN, conciergerie Airbnb à Toulouse.
Avant de rédiger un email, tu analyses la situation et tu identifies ce qu'il est pertinent et approprié de dire — et ce qu'il ne faut surtout PAS dire.
Tu réponds uniquement avec une analyse structurée, pas encore l'email.`

  const userReflexion = `Analyse ce prospect avant d'écrire l'email :
- Type de bien : ${typeBien} à ${quartier}
- Source : ${prospect.source || "annonce"}
- Ce qu'on sait : ${prospect.notes || "peu d'informations"}

Réponds en 3 points :
1. ARGUMENTS PERTINENTS : quels arguments peuvent résonner avec ce profil ? (sans inventer de données)
2. À ÉVITER : qu'est-ce qui serait inapproprié, faux ou contre-productif ?
3. TON IDÉAL : pourquoi ?`

  const reflexion = await callClaude(systemReflexion, userReflexion, 300)

  const systemRedaction = `Tu rédiges un email de prospection pour FULLBNBZEN, conciergerie Airbnb à Toulouse fondée par Dimitri et Néïsia.

EXEMPLE PARFAIT à suivre :
---
Bonjour,

Je me permets de vous contacter suite à la découverte de votre annonce.

Nous sommes Dimitri et Néïsia, fondateurs de FULLBNBZEN, une conciergerie Airbnb à Toulouse.

Votre [TYPE DE BIEN] à [QUARTIER] nous a semblé avoir un beau potentiel pour la location saisonnière. Nous prenons en charge tout ce qui peut être contraignant : création de l'annonce, accueil des voyageurs, ménage, linge, communication 24h/24. Vous percevez vos revenus chaque mois, sans vous occuper de quoi que ce soit. Vous pouvez découvrir notre fonctionnement sur fullbnbzen.com.

Plusieurs propriétaires toulousains nous font déjà confiance et notre note 5/5 sur Google en témoigne.

Nous sommes disponibles pour un échange rapide si cela vous intéresse, sans engagement de votre part.

Bien à vous,

Dimitri & Néïsia
FULLBNBZEN
---

RÈGLES ABSOLUES : zéro chiffre, zéro pourcentage, zéro statistique. Adapter UNIQUEMENT le type de bien et le quartier. Signer "Dimitri & Néïsia" puis "FULLBNBZEN" sur la ligne suivante.`

  const userRedaction = `En t'appuyant sur cette analyse :
${reflexion}

Adapte l'email exemple pour : ${typeBien} à ${quartier} (source: ${prospect.source || "annonce"}).
Remplace uniquement [TYPE DE BIEN] et [QUARTIER]. Garde tout le reste identique.`

  const raw = await callClaude(systemRedaction, userRedaction, 500)
  return sanitizeProspectEmail(raw)
}

// ── Email de suivi post-RDV ──
export const genEmailSuiviClaude = async (prospect, notes) => {
  const system = `Tu rédiges un email de suivi post-RDV pour FULLBNBZEN, conciergerie Airbnb à Toulouse.

Ton : chaleureux, professionnel, naturel. Pas de chiffres inventés. Signer uniquement Dimitri & Néïsia.

Structure : 1) Remerciement sincère pour l'échange 2) Résumé des points évoqués (basé sur les notes) 3) Prochaine étape concrète`

  const user = `Email de suivi post-RDV pour ${prospect.nom}${prospect.adresse ? ` — ${prospect.adresse}` : ""}${prospect.typeLogement ? ` (${prospect.typeLogement})` : ""}
Notes du RDV : ${notes || "Échange positif, prospect intéressé"}
Adapte le contenu aux notes. 3 paragraphes max. Signer Dimitri & Néïsia.`

  return await callClaude(system, user, 400)
}
